import {IconArrowBarToDown, IconArrowBarToUp, IconEye, IconEyeOff, IconHeart, IconUserPlus} from "@tabler/icons-react";
import PropTypes from "prop-types";
import {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import useScrollLock from "@/hooks/useScrollLock";
import appStore from "../../../assets/images/app-store.svg";
import playStore from "../../../assets/images/play-store.png";
import facebookIcon from "../../../assets/images/social-media/facebookIcon.svg";
import instagramIcon from "../../../assets/images/social-media/instagramIcon.svg";
import tiktokIcon from "../../../assets/images/social-media/tiktokIcon.svg";
import {DownloadApp, SocialMedia} from "../../../config/config";
import useGetUserWallet from "../../../hooks/wallet/useGetUserWallet";
import {MenuVertical} from "../../../Layout/components/BaseMenu/arrayMenu";
import {useAuthStore} from "../../../store/useAuth.store";
import {useBalanceStore} from "../../../store/useBalance.store";
import useCurrencyStore from "../../../store/useCurrency.store";
import {useUserSupport} from "../../../store/useUserSupport.store";
import {getUserAvatarImage} from "../../../util/avatarUtils";
import {useLogo} from "../../../util/logoUtils";
import styles from "./MobileSidebar.module.css";

const MAIN_KEYS = ["/", "/casino", "games", "/sports", "/lottery"];
const GAMES_KEYS = ["/game-feed", "/horses", "/bingo"];

const MobileSidebar = ({isOpen, onClose}) => {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const menuItems = MenuVertical();
  const logo = useLogo();
  const [expandedKeys, setExpandedKeys] = useState([]);
  const sidebarRef = useRef(null);

  const authStatus = useAuthStore(state => state.authStatus);
  const user = useAuthStore(state => state.user);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const handleSupportModal = useUserSupport(state => state.handleSupportModal);
  const handleBankAccountModal = useBalanceStore(state => state.handleBankAccountModal);
  const handleWithdrawalModal = useBalanceStore(state => state.handleWithdrawalModal);

  const {balance, accounts, getAccountByCurrency} = useGetUserWallet();
  const {selectedCurrency} = useCurrencyStore();
  const [balanceVisible, setBalanceVisible] = useState(false);

  const firstName = user?.given_name || user?.firstName || "";
  const lastName = user?.family_name || user?.lastName || "";
  const username = user?.preferred_username || user?.email || "";
  const avatarSrc = getUserAvatarImage(user);

  const selectedAccount = selectedCurrency ? getAccountByCurrency(selectedCurrency.id) : balance;
  const displayBalance = selectedAccount?.formattedBalance || balance?.formattedBalance || "Bs. 0,00";

  const otherAccounts = accounts.filter(
    acc => acc.balance > 0 && acc.currency !== (selectedAccount?.currency || balance?.currency)
  );

  // Group menu items into sections
  const {mainItems, gamesItems, actionItems} = useMemo(() => {
    const main = [];
    const games = [];
    const actions = [];

    for (const item of menuItems) {
      if (MAIN_KEYS.includes(item.key)) main.push(item);
      else if (GAMES_KEYS.includes(item.key)) games.push(item);
      else if (item.key !== "/support") actions.push(item);
    }

    return {mainItems: main, gamesItems: games, actionItems: actions};
  }, [menuItems]);

  useScrollLock(isOpen);

  useEffect(() => {
    const handleEscape = e => {
      if (e.key === "Escape") onClose();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const handleNavigation = item => {
    if (item.onClick) {
      item.onClick();
      onClose();
      return;
    }

    if (authStatus !== "authenticated" && item.key !== "/" && item.key !== "/support") {
      useAuthStore.getState().handleAuthForm("login");
      handleShowAuthModal();
      onClose();
      return;
    }

    navigate(item.key);
    onClose();
    setTimeout(() => {
      window.scrollTo({top: 0, behavior: "smooth"});
    }, 100);
  };

  const toggleExpand = key => {
    setExpandedKeys(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
  };

  const renderMenuItem = (item, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedKeys.includes(item.key);

    return (
      <li
        key={item.key}
        className={styles.menuItem}
      >
        <button
          type="button"
          className={`${styles.menuItemButton} ${depth > 0 ? styles.subMenuItem : ""}`}
          style={depth > 0 ? {paddingLeft: `${20 + depth * 16}px`} : undefined}
          onClick={() => (hasChildren ? toggleExpand(item.key) : handleNavigation(item))}
        >
          {item.icon && <span className={styles.menuItemIcon}>{item.icon}</span>}
          <span className={styles.menuItemLabel}>{item.label}</span>
          {hasChildren && <span className={`${styles.menuItemArrow} ${isExpanded ? styles.expanded : ""}`}>▸</span>}
        </button>
        {hasChildren && isExpanded && (
          <ul className={styles.subMenu}>{item.children.map(child => renderMenuItem(child, depth + 1))}</ul>
        )}
      </li>
    );
  };

  const renderSection = (label, items) => (
    <div className={styles.navSection}>
      <div className={styles.sectionLabel}>{label}</div>
      <ul className={styles.menu}>{items.map(item => renderMenuItem(item))}</ul>
    </div>
  );

  return (
    <>
      {isOpen && (
        <div
          className={styles.backdrop}
          onClick={onClose}
          role="presentation"
        />
      )}

      <div
        className={`${styles.sidebar} ${isOpen ? styles.open : ""}`}
        ref={sidebarRef}
      >
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <img
              src={logo}
              alt="Logo"
              className={styles.logo}
              onClick={() => {
                navigate("/");
                onClose();
              }}
            />
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Cerrar menú"
            >
              ✕
            </button>
          </div>

          {authStatus === "authenticated" && (
            <div className={styles.userPanel}>
              <div
                className={styles.userPanelTop}
                onClick={() => {
                  navigate(`/u/${user?.alias || "me"}`);
                  onClose();
                }}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    navigate(`/u/${user?.alias || "me"}`);
                    onClose();
                  }
                }}
                style={{cursor: "pointer"}}
              >
                <img
                  src={avatarSrc}
                  alt=""
                  className={styles.userAvatar}
                />
                <div className={styles.userDetails}>
                  <div className={styles.userName}>
                    {firstName || lastName ? `${firstName} ${lastName}`.trim() : username}
                  </div>
                  {(firstName || lastName) && username && <div className={styles.userUsername}>@{username}</div>}
                </div>
              </div>

              <div className={styles.balanceRow}>
                <div className={styles.userBalance}>{balanceVisible ? displayBalance : "****"}</div>
                <button
                  type="button"
                  className={styles.eyeToggle}
                  onClick={() => setBalanceVisible(prev => !prev)}
                  aria-label={balanceVisible ? "Ocultar saldo" : "Mostrar saldo"}
                >
                  {balanceVisible ? (
                    <IconEye
                      size={18}
                      stroke={2}
                      color="var(--text-muted)"
                    />
                  ) : (
                    <IconEyeOff
                      size={18}
                      stroke={2}
                      color="var(--text-muted)"
                    />
                  )}
                </button>
              </div>

              {balanceVisible && otherAccounts.length > 0 && (
                <div className={styles.otherBalances}>
                  {otherAccounts.map(acc => (
                    <span
                      key={acc.currency}
                      className={styles.otherBalanceItem}
                    >
                      {acc.formattedBalance}
                    </span>
                  ))}
                </div>
              )}

              <div className={styles.walletActions}>
                <button
                  type="button"
                  className={styles.depositButton}
                  onClick={() => {
                    handleBankAccountModal();
                    onClose();
                  }}
                >
                  <IconArrowBarToDown
                    size={16}
                    stroke={2.5}
                  />
                  {t("floatBtnDeposit")}
                </button>
                <button
                  type="button"
                  className={styles.withdrawButton}
                  onClick={() => {
                    handleWithdrawalModal();
                    onClose();
                  }}
                >
                  <IconArrowBarToUp
                    size={16}
                    stroke={2}
                  />
                  {t("floatBtnWithdrawl")}
                </button>
              </div>

              <button
                type="button"
                className={styles.inviteButton}
                onClick={() => {
                  navigate("/user/referrals");
                  onClose();
                }}
              >
                <IconUserPlus
                  size={14}
                  stroke={2}
                />
                {t("inviteFriend")}
              </button>

              <button
                type="button"
                className={styles.favoritesButton}
                onClick={() => {
                  navigate("/favorites");
                  onClose();
                }}
              >
                <IconHeart size={13} />
                {t("sidebarMyFavorites")}
              </button>
            </div>
          )}

          {authStatus !== "authenticated" && (
            <div className={styles.authButtons}>
              <button
                type="button"
                className={styles.loginButton}
                onClick={() => {
                  useAuthStore.getState().handleAuthForm("login");
                  handleShowAuthModal();
                  onClose();
                }}
              >
                {t("homeDisplayLoginBtn")}
              </button>
              <button
                type="button"
                className={styles.registerButton}
                onClick={() => {
                  useAuthStore.getState().handleAuthForm("registration");
                  handleShowAuthModal();
                  onClose();
                }}
              >
                {t("homeDisplayRegisterBtn")}
              </button>
            </div>
          )}
        </div>

        <nav className={styles.nav}>
          {renderSection(t("sidebarSectionMain", "PRINCIPAL"), mainItems)}
          {gamesItems.length > 0 && renderSection(t("sidebarSectionGames", "JUEGOS"), gamesItems)}
          {actionItems.length > 0 && renderSection(t("sidebarSectionMore", "MÁS"), actionItems)}

          {/* Actions section: Support + Download */}
          <div className={styles.navSection}>
            <div className={styles.sectionLabel}>{t("sidebarSectionActions", "ACCIONES")}</div>
            <ul className={styles.menu}>
              <li className={styles.menuItem}>
                <button
                  type="button"
                  className={styles.menuItemButton}
                  onClick={() => {
                    handleSupportModal();
                    onClose();
                  }}
                >
                  <span className={styles.menuItemIcon}>💬</span>
                  <span className={styles.menuItemLabel}>{t("footerTitleSupport")}</span>
                </button>
              </li>
              <li className={styles.menuItem}>
                <div className={styles.socialRow}>
                  <a
                    href={SocialMedia.FACEBOOK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialLink}
                    aria-label="Facebook"
                  >
                    <img
                      src={facebookIcon}
                      alt="Facebook"
                      className={styles.socialIcon}
                    />
                  </a>
                  <a
                    href={SocialMedia.INSTAGRAM}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialLink}
                    aria-label="Instagram"
                  >
                    <img
                      src={instagramIcon}
                      alt="Instagram"
                      className={styles.socialIcon}
                    />
                  </a>
                  <a
                    href={SocialMedia.TIKTOK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialLink}
                    aria-label="TikTok"
                  >
                    <img
                      src={tiktokIcon}
                      alt="TikTok"
                      className={styles.socialIcon}
                    />
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </nav>

        <div className={styles.sidebarFooter}>
          <a
            href={DownloadApp.ANDROID}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.downloadLink}
          >
            <img
              src={playStore}
              alt="Google Play"
              className={styles.storeBadge}
            />
          </a>
          <a
            href={DownloadApp.IOS}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.downloadLink}
          >
            <img
              src={appStore}
              alt="App Store"
              className={styles.storeBadge}
            />
          </a>
        </div>
      </div>
    </>
  );
};

MobileSidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MobileSidebar;
